import { useState, useEffect, useCallback } from "react";

export type CookiePreferences = {
  essential: boolean; // Always true, required
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
};

const COOKIE_CONSENT_KEY = "cookie-consent";
const COOKIE_PREFERENCES_KEY = "cookie-preferences";

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
  functional: false,
};

export function useCookieConsent() {
  const [hasConsented, setHasConsented] = useState<boolean | null>(null);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (consent === null) {
      setHasConsented(null);
      setShowBanner(true);
    } else {
      setHasConsented(consent === "true");
      setShowBanner(false);
      if (savedPreferences) {
        try {
          setPreferences(JSON.parse(savedPreferences));
        } catch {
          setPreferences(defaultPreferences);
        }
      }
    }
  }, []);

  const acceptAll = useCallback(() => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));
    setHasConsented(true);
    setPreferences(allAccepted);
    setShowBanner(false);
  }, []);

  const rejectAll = useCallback(() => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "false");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(defaultPreferences));
    setHasConsented(false);
    setPreferences(defaultPreferences);
    setShowBanner(false);
  }, []);

  const savePreferences = useCallback((newPreferences: CookiePreferences) => {
    const prefs = { ...newPreferences, essential: true }; // Essential always true
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setHasConsented(true);
    setPreferences(prefs);
    setShowBanner(false);
  }, []);

  const resetConsent = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    setHasConsented(null);
    setPreferences(defaultPreferences);
    setShowBanner(true);
  }, []);

  return {
    hasConsented,
    preferences,
    showBanner,
    acceptAll,
    rejectAll,
    savePreferences,
    resetConsent,
    setShowBanner,
  };
}
